import React, { useState, useEffect, useRef } from 'react';
import { FaRupeeSign, FaChartLine, FaRocket, FaStar, FaQuestionCircle } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { Chart, registerables } from 'chart.js';
import 'chart.js/auto';
import { Helmet } from 'react-helmet';

Chart.register(...registerables);

const SIPCalculator = () => {
  const navigate = useNavigate();
  const [monthlyInvestment, setMonthlyInvestment] = useState('');
  const [investmentPeriod, setInvestmentPeriod] = useState('');
  const [expectedReturn, setExpectedReturn] = useState('');
  const [totalInvestment, setTotalInvestment] = useState(0);
  const [totalValue, setTotalValue] = useState(0);
  const [totalProfit, setTotalProfit] = useState(0);
  const [chartData, setChartData] = useState({});
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const calculateSIP = () => {
    if (!monthlyInvestment || !investmentPeriod || !expectedReturn) return;

    const monthlyInvestmentNum = parseFloat(monthlyInvestment);
    const investmentPeriodNum = parseFloat(investmentPeriod);
    const expectedReturnNum = parseFloat(expectedReturn) / 100 / 12;

    // Calculate total investment
    const totalInvestmentNum = monthlyInvestmentNum * investmentPeriodNum * 12;
    setTotalInvestment(totalInvestmentNum);

    // Calculate future value using SIP formula
    const totalValueNum = monthlyInvestmentNum * 
      ((Math.pow(1 + expectedReturnNum, investmentPeriodNum * 12) - 1) / expectedReturnNum) * 
      (1 + expectedReturnNum);

    setTotalValue(totalValueNum);
    setTotalProfit(totalValueNum - totalInvestmentNum);

    // Generate data for chart
    const years = Array.from({ length: investmentPeriodNum }, (_, i) => i + 1);
    const values = years.map(year => {
      const months = year * 12;
      return monthlyInvestmentNum * 
        ((Math.pow(1 + expectedReturnNum, months) - 1) / expectedReturnNum) * 
        (1 + expectedReturnNum);
    });

    setChartData({
      labels: years.map(y => `Year ${y}`),
      datasets: [{
        label: 'Investment Growth',
        data: values,
        fill: true,
        borderColor: '#6366f1',
        tension: 0.1,
        backgroundColor: 'rgba(99, 102, 241, 0.1)'
      }]
    });
  };

  useEffect(() => {
    if (chartData.labels && chartRef.current) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      chartInstance.current = new Chart(chartRef.current, {
        type: 'line',
        data: chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: function(value) {
                  return '₹' + value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                }
              }
            }
          },
          plugins: {
            legend: {
              position: 'top'
            },
            title: {
              display: true,
              text: 'SIP Growth Over Time'
            }
          }
        }
      });
    }
  }, [chartData]);

  const handleViewGrowthChart = () => {
    navigate(`/indian-finance/sip-growth/${monthlyInvestment}/${investmentPeriod}/${expectedReturn}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>SIP Calculator - Systematic Investment Plan</title>
        <meta name="description" content="Calculate your SIP investment returns with our Systematic Investment Plan calculator. Plan your monthly investments and see how they grow over time." />
        <meta property="og:title" content="SIP Calculator - Systematic Investment Plan" />
        <meta property="og:description" content="Calculate your SIP investment returns with our Systematic Investment Plan calculator. Plan your monthly investments and see how they grow over time." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://your-domain.com/sip-calculator-preview.png" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content="SIP Calculator - Systematic Investment Plan" />
        <meta property="twitter:description" content="Calculate your SIP investment returns with our Systematic Investment Plan calculator. Plan your monthly investments and see how they grow over time." />
        <meta property="article:tag" content="SIP Calculator" />
        <meta property="article:tag" content="Investment Planning" />
        <meta property="article:tag" content="Mutual Funds" />
      </Helmet>
      
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4">
            <FaRocket className="text-purple-600 text-3xl" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">SIP Calculator</h1>
          <p className="text-gray-600">Calculate your SIP investment returns and plan your future wealth</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Monthly Investment Amount
              </label>
              <div className="relative">
                <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  value={monthlyInvestment}
                  onChange={(e) => setMonthlyInvestment(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Enter monthly investment"
                />
              </div>
            </div>  

            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Investment Period (Years)
              </label>
              <input
                type="number"
                value={investmentPeriod}
                onChange={(e) => setInvestmentPeriod(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Enter investment period"
              />
            </div>

            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Expected Annual Return (%)
              </label>
              <input
                type="number"
                value={expectedReturn}
                onChange={(e) => setExpectedReturn(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Enter expected return"
              />
            </div>
          </div>

          <button
            onClick={calculateSIP}
            className="mt-6 w-full bg-gradient-to-r from-purple-600 to-blue-600 text-green-600 py-3 px-6 rounded-lg hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-300"
          >
            <span className="inline-flex items-center justify-center">
              <span className="mr-2">Calculate Returns</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </button>
        </div>

        {totalInvestment > 0 && (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900">Total Investment</h3>
                  <p className="mt-2 text-2xl font-bold text-purple-600">
                    ₹{totalInvestment.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </p>
                </div>

                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900">Total Value</h3>
                  <p className="mt-2 text-2xl font-bold text-green-600">
                    ₹{totalValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </p>
                </div>

                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900">Total Profit</h3>
                  <p className="mt-2 text-2xl font-bold text-green-600">
                    ₹{totalProfit.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </p>
                </div>
              </div>

              <div className="mt-8">
                <canvas ref={chartRef} className="w-full h-64" />
              </div>

              <div className="mt-8 text-center">
                <button
                  onClick={handleViewGrowthChart}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
                >
                  <FaChartLine className="mr-2" />
                  <span className="font-semibold">View SIP Growth Chart</span>
                </button>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Features</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-purple-50 rounded-lg">
                  <FaStar className="text-purple-600 text-2xl mb-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Compound Interest</h3>
                  <p className="text-gray-600">Calculate the power of compounding on your investments</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <FaStar className="text-purple-600 text-2xl mb-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Monthly Investments</h3>
                  <p className="text-gray-600">Plan your regular monthly investments</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <FaStar className="text-purple-600 text-2xl mb-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Custom Returns</h3>
                  <p className="text-gray-600">Set your expected return rate</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <FaStar className="text-purple-600 text-2xl mb-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Investment Period</h3>
                  <p className="text-gray-600">Plan for short-term or long-term goals</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Benefits of SIP</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <FaStar className="text-purple-600 text-xl mr-3 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Disciplined Investing</h3>
                    <p className="text-gray-600">Regular investments help build a disciplined investment habit</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaStar className="text-purple-600 text-xl mr-3 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Rupee Cost Averaging</h3>
                    <p className="text-gray-600">Buy more units when prices are low and fewer when prices are high</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaStar className="text-purple-600 text-xl mr-3 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Power of Compounding</h3>
                    <p className="text-gray-600">Earn returns on your returns, leading to exponential growth</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaStar className="text-purple-600 text-xl mr-3 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Flexibility</h3>
                    <p className="text-gray-600">Pause, resume, or modify your investments as needed</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Use</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <FaQuestionCircle className="text-purple-600 text-xl mr-3 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Step 1: Enter Monthly Investment</h3>
                    <p className="text-gray-600">Input the amount you want to invest monthly</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaQuestionCircle className="text-purple-600 text-xl mr-3 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Step 2: Set Investment Period</h3>
                    <p className="text-gray-600">Choose how many years you want to invest</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaQuestionCircle className="text-purple-600 text-xl mr-3 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Step 3: Set Expected Return</h3>
                    <p className="text-gray-600">Enter your expected annual return rate</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FaQuestionCircle className="text-purple-600 text-xl mr-3 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Step 4: Calculate Returns</h3>
                    <p className="text-gray-600">Click calculate to see your investment growth</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">FAQs</h2>
              <div className="space-y-4">
                <div className="border-l-4 border-purple-600 pl-4">
                  <h3 className="font-semibold text-gray-900">What is SIP?</h3>
                  <p className="text-gray-600">SIP stands for Systematic Investment Plan, a method of investing a fixed amount at regular intervals.</p>
                </div>
                <div className="border-l-4 border-purple-600 pl-4">
                  <h3 className="font-semibold text-gray-900">How does SIP work?</h3>
                  <p className="text-gray-600">You invest a fixed amount at regular intervals (monthly/quarterly) in mutual funds or other investment options.</p>
                </div>
                <div className="border-l-4 border-purple-600 pl-4">
                  <h3 className="font-semibold text-gray-900">Is SIP better than lump sum?</h3>
                  <p className="text-gray-600">SIP is better for disciplined investing and rupee cost averaging, while lump sum is better for larger amounts.</p>
                </div>
                <div className="border-l-4 border-purple-600 pl-4">
                  <h3 className="font-semibold text-gray-900">Can I change my SIP amount?</h3>
                  <p className="text-gray-600">Yes, you can increase or decrease your SIP amount based on your financial situation.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SIPCalculator;
